import React from 'react';
import { MATERIALS, ACTIONS } from '../../game/constants';
import type { ParticleType } from '../../game/constants';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { Locale } from '../../i18n';
import { getToolLabel, t } from '../../i18n';

interface MainToolbarProps {
    currentMaterial: ParticleType;
    selectedParticle: ParticleType;
    setSelectedParticle: (type: ParticleType) => void;
    isMaterialPanelOpen: boolean;
    setIsMaterialPanelOpen: (isOpen: boolean) => void;
    currentTool: ParticleType;
    isToolsPanelOpen: boolean;
    setIsToolsPanelOpen: (isOpen: boolean) => void;
    enableGlow: boolean;
    setEnableGlow: (enable: boolean) => void;
    onClear: () => void;
    toolbarRef: React.RefObject<HTMLDivElement | null>;
    closePanels: () => void;
    locale: Locale;
}

export const MainToolbar: React.FC<MainToolbarProps> = ({
    currentMaterial,
    selectedParticle,
    setSelectedParticle,
    isMaterialPanelOpen,
    setIsMaterialPanelOpen,
    currentTool,
    isToolsPanelOpen,
    setIsToolsPanelOpen,
    enableGlow,
    setEnableGlow,
    onClear,
    toolbarRef,
    closePanels,
    locale
}) => {
    const isMobile = useIsMobile();

    return (
        <div 
            ref={toolbarRef}
            className="bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 p-2 rounded-2xl shadow-2xl flex items-center gap-2 overflow-x-auto max-w-full no-scrollbar">
                {/* Current Material & More */}
                {(() => {
                    const tool = MATERIALS.find(t => t.type === currentMaterial) || MATERIALS[0];
                    if (!tool) return null;
                    const toolLabel = getToolLabel(locale, tool.type, tool.label);
                    const isSelected = selectedParticle === tool.type;
                    
                    return (
                        <button
                            onClick={() => {
                                setSelectedParticle(tool.type);
                                closePanels();
                            }}
                            className={`
                                relative px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap
                                flex flex-col items-center gap-1 min-w-[4rem] group cursor-pointer
                                ${isSelected
                                    ? 'bg-teal-500/15 text-teal-300 shadow-[0_0_15px_-3px_rgba(20,184,166,0.3)] ring-1 ring-teal-500/30' 
                                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}
                            `}
                            title={`${toolLabel} (${tool.shortcut.toUpperCase()})`}
                        >
                            <span className="text-sm">{toolLabel}</span>
                            {!isMobile && (
                                <span className={`text-[10px] font-mono transition-colors ${
                                    isSelected ? 'text-teal-500/70' : 'text-slate-600 group-hover:text-slate-500'
                                }`}>
                                    {tool.shortcut.toUpperCase()}
                                </span>
                            )}
                        </button>
                    );
                })()}

                <button
                    onClick={() => {
                        if (isMaterialPanelOpen) {
                            setIsMaterialPanelOpen(false);
                        } else {
                            setIsMaterialPanelOpen(true);
                            setIsToolsPanelOpen(false);
                        }
                    }}
                    className={`
                        relative px-2 py-2 rounded-xl text-sm font-medium transition-all duration-200
                        flex flex-col items-center justify-center gap-1 min-w-[2.5rem] group cursor-pointer
                        ${isMaterialPanelOpen ? 'bg-slate-800 text-slate-200' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}
                    `}
                    title={t(locale, "moreMaterials")}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                            className={`transition-transform duration-300 ${isMaterialPanelOpen ? 'rotate-180' : ''}`}>
                        <path d="m18 15-6-6-6 6"/>
                    </svg>
                </button>

                <div className="w-px h-8 bg-slate-700/50 mx-2 shrink-0"></div>

                {/* Current Tool & More */}
                {(() => {
                    const tool = ACTIONS.find(t => t.type === currentTool) || ACTIONS[0];
                    if (!tool) return null;
                    const toolLabel = getToolLabel(locale, tool.type, tool.label);
                    const isSelected = selectedParticle === tool.type;
                    
                    return (
                        <button
                            onClick={() => {
                                setSelectedParticle(tool.type);
                                closePanels();
                            }}
                            className={`
                                relative px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap
                                flex flex-col items-center gap-1 min-w-[4rem] group cursor-pointer
                                ${isSelected
                                    ? 'bg-amber-500/15 text-amber-300 shadow-[0_0_15px_-3px_rgba(245,158,11,0.3)] ring-1 ring-amber-500/30' 
                                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}
                            `}
                            title={`${toolLabel} (${tool.shortcut.toUpperCase()})`}
                        >
                            <span className="text-sm">{toolLabel}</span>
                            {!isMobile && (
                                <span className={`text-[10px] font-mono transition-colors ${
                                    isSelected ? 'text-amber-500/70' : 'text-slate-600 group-hover:text-slate-500'
                                }`}>
                                    {tool.shortcut.toUpperCase()}
                                </span>
                            )}
                        </button>
                    );
                })()}

                <button
                    onClick={() => {
                        if (isToolsPanelOpen) {
                            setIsToolsPanelOpen(false);
                        } else {
                            setIsToolsPanelOpen(true);
                            setIsMaterialPanelOpen(false);
                        }
                    }}
                    className={`
                        relative px-2 py-2 rounded-xl text-sm font-medium transition-all duration-200
                        flex flex-col items-center justify-center gap-1 min-w-[2.5rem] group cursor-pointer
                        ${isToolsPanelOpen ? 'bg-slate-800 text-slate-200' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}
                    `}
                    title={t(locale, "moreTools")}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                            className={`transition-transform duration-300 ${isToolsPanelOpen ? 'rotate-180' : ''}`}>
                        <path d="m18 15-6-6-6 6"/>
                    </svg>
                </button>
                
                <div className="w-px h-8 bg-slate-700/50 mx-2 shrink-0"></div>
                
                {/* Settings Group */}
                <button
                    onClick={() => {
                        setEnableGlow(!enableGlow);
                        closePanels();
                    }}
                    className={`
                        px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200
                        flex flex-col items-center justify-center gap-1 min-w-[4rem] cursor-pointer
                        ${enableGlow
                            ? 'bg-purple-500/15 text-purple-300 shadow-[0_0_15px_-3px_rgba(168,85,247,0.3)] ring-1 ring-purple-500/30'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}
                    `}
                >
                    <span className="leading-none py-1">{t(locale, "glow")}</span>
                    {!isMobile && <span className="text-[10px] opacity-0">.</span>}
                </button>

                <button
                    onClick={() => {
                        onClear();
                        closePanels();
                    }}
                    className={`
                        px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200
                        flex flex-col items-center justify-center gap-1 min-w-[4rem] cursor-pointer
                        text-slate-400 hover:text-rose-400 hover:bg-slate-800
                    `}
                    title={`${t(locale, "clear")} (X)`}
                >
                    <span className="leading-none py-1">{t(locale, "clear")}</span>
                    {!isMobile && <span className="text-[10px] font-mono transition-colors text-slate-600 group-hover:text-slate-500">X</span>}
                </button>
                
                {/* CSS for hiding scrollbar if needed */}
                <style>{`
                    .no-scrollbar::-webkit-scrollbar {
                        display: none;
                    }
                    .no-scrollbar {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                `}</style>
            </div>
    );
};
